"use client"

import { useEffect, useState, useMemo } from "react"
import { supabase } from "@/lib/supabase"
import { useAuth } from "@/contexts/AuthContext"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { Separator } from "@/components/ui/separator"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { User, Check, Users, Gift, Copy, Sparkles } from "lucide-react"
import { Dialog, DialogContent, DialogFooter } from "@/components/ui/dialog"
import { NewPricingSection } from "@/components/new-pricing-section"

const pricingPlans = [
  {
    id: "6d4ecf79-200c-4efc-85a9-e1205386d362",
    name: "Free Plan",
    priceLabel: "0$ /",
    features: [
      "Capture up to 200 fans (emails/texts/socials)",
      "1 campaign per month (blast your fans with a track, merch drop, or show)",
      "Basic fan analytics (total fans, engagement rate)",
      "1 smart link / QR code (to promote your music)",
    ],
    cta: "Join for Free",
    link: "/settings",
  },
  {
    id: "6b8ff00f-be5d-4da4-b007-06b4e27a2226",
    name: "Growth Plan",
    priceLabel: "$19.00 /",
    features: [
      "Everything in Free, plus:",
      "Capture up to 2,500 fans.",
      "Unlimited campaigns (email + SMS).",
      "Advanced fan analytics (top listeners, engagement by release, fan location).",
      "Custom fan tags & segmentation (separate casual listeners vs superfans).",
      "3 smart links / QR codes (multi-project promos).",
      "Priority email support.",
    ],
    cta: "Join Growth Plan",
    priceId: "price_1S77FM4H36Uva1e4k8rSlQZI",
    popular: true,
  },
  {
    id: "2aadfdd1-ec49-4e7f-a1ce-c3801fa0180e",
    name: "Pro Plan",
    priceLabel: "$49.00 /",
    features: [
      "Everything in Growth, plus:",
      "Capture up to 10,000 fans.",
      "Automated fan journeys (welcome flows, follow-ups after releases, birthday/ anniversary messages).",
      "Merch + ticketing integrations (sell directly to your fans, track buyer, implemented later).",
      "Collaboration tools (cross-promos with other artists).",
      "Up to 10 smart links / QR codes.",
      "1-on-1 onboarding call.",
    ],
    cta: "Join Pro Plan",
    priceId: "price_1S77Gt4H36Uva1e4d6hMADiw",
  },
]

const Affiliates = () => {
  const { user } = useAuth()
  const [profile, setProfile] = useState(null)
  const [subscription, setSubscription] = useState(null)
  const [plan, setPlan] = useState(null)
  const [loading, setLoading] = useState(true)
  const [portalLoading, setPortalLoading] = useState(false)
  const [showUpgradeDialog, setShowUpgradeDialog] = useState(false)
  const [credits, setCredits] = useState(0)
  const [copying, setCopying] = useState(false)
  const [applying, setApplying] = useState(false)

  const referralLink = useMemo(() => {
    if (!user?.id) return ""
  const base = "https://amply.buildfast.us/register"
    return `${base}?ref=${encodeURIComponent(user.id)}`
  }, [user?.id])

  const formatNextBillingDate = (dateString) => {
    const date = new Date(dateString)
    const now = new Date()
    const diffTime = date.getTime() - now.getTime()
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))

    if (diffDays <= 7) {
      return `in ${diffDays} day${diffDays !== 1 ? "s" : ""}`
    }

    return date.toLocaleDateString(undefined, {
      month: "short",
      day: "numeric",
      year: date.getFullYear() !== now.getFullYear() ? "numeric" : undefined,
    })
  }

  // Fetch user profile, subscription, and plan
  useEffect(() => {
    if (!user) return
    setLoading(true)
    Promise.all([
      supabase.from("profiles").select("full_name, email").eq("id", user.id).maybeSingle(),
      // include billing metadata columns that server.js writes
      supabase
        .from("subscriptions")
        .select(
          "plan_id, status, session_id, customer_id, next_billing_date, payment_method_brand, payment_method_last4, payment_method_exp_month, payment_method_exp_year, invoice",
        )
        .eq("user_id", user.id)
        .maybeSingle(),
    ]).then(async ([{ data: profileData }, { data: subData }]) => {
      setProfile(profileData)
      setSubscription(subData)
      if (subData?.plan_id) {
        // Try to match plan by id or name
        let planData = null
        // Try by id
        planData = pricingPlans.find((p) => p.id === subData.plan_id)
        // If not found, try by name (fallback)
        if (!planData && subData.name) {
          planData = pricingPlans.find((p) => p.name === subData.name)
        }
        setPlan(planData)
      }
      setLoading(false)
    })
  }, [user])

  // Load current referral credits
  useEffect(() => {
    if (!user?.id) return
  fetch(`https://backend.amply.buildfast.us/referral/credits/${encodeURIComponent(user.id)}`)
      .then((r) => r.json())
      .then((j) => {
        if (j && j.success) setCredits(Number(j.credits || 0))
      })
      .catch(() => {})
  }, [user?.id])

  const handleManagePlan = async () => {
    if (!subscription?.session_id) return alert("No active subscription session found.")
    setPortalLoading(true)
    try {
  const res = await fetch("https://backend.amply.buildfast.us/create-customer-portal-session", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ sessionId: subscription.session_id }),
      })
      const { url, error } = await res.json()
      if (error) alert(error)
      else window.location.href = url
    } finally {
      setPortalLoading(false)
    }
  }

  const handleCopy = async () => {
    if (!referralLink) return
    setCopying(true)
    try {
      await navigator.clipboard.writeText(referralLink)
    } catch {}
    setTimeout(() => setCopying(false), 600)
  }

  const handleApplyCredit = async () => {
    if (!user?.id || credits <= 0) return
    setApplying(true)
    try {
  const res = await fetch("https://backend.amply.buildfast.us/referral/apply", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ userId: user.id }),
      })
      const j = await res.json()
      if (j && j.success) {
        setCredits((c) => Math.max(0, c - 1))
        alert("Referral credit applied to your next invoice (50% off for one month).")
      } else if (j?.error) {
        alert(j.error)
      }
    } finally {
      setApplying(false)
    }
  }

  if (!user)
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center space-y-4">
          <div className="w-16 h-16 mx-auto rounded-full bg-muted flex items-center justify-center">
            <User className="h-8 w-8 text-muted-foreground" />
          </div>
          <p className="text-muted-foreground">Please log in to access your referral dashboard.</p>
        </div>
      </div>
    )

  if (loading)
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center space-y-4">
          <div className="w-8 h-8 mx-auto border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
          <p className="text-muted-foreground">Loading your referral dashboard...</p>
        </div>
      </div>
    )

  return (
    <>
      <Dialog open={showUpgradeDialog} onOpenChange={setShowUpgradeDialog}>
        <DialogContent className="glass-card max-w-[90vw] max-h-[90vh] overflow-y-auto">
          <NewPricingSection />
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowUpgradeDialog(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

  <div className="min-h-screen bg-background dark:text-white">
        {/* Hero Section */}
        <div className="relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-primary/20 via-transparent to-accent/10"></div>
          <div className="relative max-w-7xl mx-auto px-6 py-16">
            <div className="text-center space-y-6">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 border border-primary/20">
                <Sparkles className="h-4 w-4 text-primary" />
                <span className="text-sm font-medium text-primary">Artist Referral Program</span>
              </div>
              <h1 className="text-5xl md:text-6xl font-bold text-balance dark:text-white">
                <span className="gradient-text">Grow Together</span>
                <br />
                <span className="text-foreground dark:text-white">Earn Together</span>
              </h1>
              <p className="text-xl text-muted-foreground max-w-2xl mx-auto text-balance">
                Invite fellow artists to Amply and unlock exclusive rewards. Every successful referral earns you both
                50% off for one month.
              </p>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="max-w-4xl mx-auto px-6 pb-16">
          {/* Stats Cards */}
          <div className="grid md:grid-cols-2 gap-6 mb-12">
            <Card className="glass-card glow-effect">
              <CardContent className="p-8">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full bg-primary/20 flex items-center justify-center">
                    <Gift className="h-6 w-6 text-primary" />
                  </div>
                  <div>
                    <p className="text-3xl font-bold text-foreground dark:text-white">{credits}</p>
                    <p className="text-muted-foreground">Available Credits</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="glass-card">
              <CardContent className="p-8">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full bg-accent/20 flex items-center justify-center">
                    <Users className="h-6 w-6 text-accent" />
                  </div>
                  <div>
                    <p className="text-3xl font-bold text-foreground dark:text-white">50%</p>
                    <p className="text-muted-foreground">Discount Per Referral</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Referral Program Card */}
          <Card className="glass-card border-primary/20">
            <CardHeader className="pb-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center">
                  <Users className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <CardTitle className="text-2xl text-foreground dark:text-white">Share Your Referral Link</CardTitle>
                  <CardDescription className="text-muted-foreground">
                    Invite artists to join Amply and earn rewards together
                  </CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-8">
              {/* Referral Link Section */}
              <div className="space-y-4">
                <Label className="text-sm font-medium text-foreground dark:text-white">Your unique referral link</Label>
                <div className="flex gap-3">
                  <Input readOnly value={referralLink} className="bg-muted/50 border-border font-mono text-sm dark:text-black" />
                  <Button
                    onClick={handleCopy}
                    disabled={!referralLink || copying}
                    className="bg-primary hover:bg-primary/90 border border-white text-primary-foreground px-6"
                  >
                    {copying ? (
                      <>
                        <Check className="h-4 w-4 mr-2" />
                        Copied!
                      </>
                    ) : (
                      <>
                        <Copy className="h-4 w-4 mr-2" />
                        Copy
                      </>
                    )}
                  </Button>
                </div>
                <p className="text-sm text-muted-foreground">
                  When an artist signs up using your link and activates a paid plan, you both receive 50% off for one
                  month. Each successful referral adds another month of discount to your account.
                </p>
              </div>

              <Separator className="bg-border" />

              {/* Credits Section */}
              <div className="space-y-6">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="font-semibold text-foreground dark:text-white">Available Discount Credits</h3>
                    <p className="text-sm text-muted-foreground">
                      Apply your earned credits to get 50% off your next invoice
                    </p>
                  </div>
                  <Badge variant="secondary" className="text-lg px-4 py-2 bg-primary/10 text-primary border-primary/20">
                    {credits}
                  </Badge>
                </div>

                <Button
                  onClick={handleApplyCredit}
                  disabled={applying || credits <= 0}
                  className="border border-white w-full bg-primary hover:bg-primary/90 text-primary-foreground py-6 text-base font-medium"
                  size="lg"
                >
                  {applying ? (
                    <>
                      <div className="w-4 h-4 mr-2 border-2 border-primary-foreground border-t-transparent rounded-full animate-spin"></div>
                      Applying Credit...
                    </>
                  ) : (
                    <>
                      <Gift className="h-5 w-5 mr-2" />
                      Apply 50% Off to Next Invoice
                    </>
                  )}
                </Button>

                {credits === 0 && (
                  <div className="text-center p-6 rounded-lg bg-muted/30 border border-border">
                    <p className="text-muted-foreground">
                      No credits available. Start referring artists to earn discount credits!
                    </p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* How It Works Section */}
          <div className="mt-16 space-y-8">
            <div className="text-center">
              <h2 className="text-3xl font-bold text-foreground dark:text-white mb-4">How It Works</h2>
              <p className="text-muted-foreground">Simple steps to start earning referral rewards</p>
            </div>

            <div className="grid md:grid-cols-3 gap-8">
              <div className="text-center space-y-4">
                <div className="w-16 h-16 mx-auto rounded-full bg-primary/20 flex items-center justify-center">
                  <span className="text-2xl font-bold text-primary">1</span>
                </div>
                <h3 className="text-xl font-semibold text-foreground dark:text-white">Share Your Link</h3>
                <p className="text-muted-foreground">Copy your unique referral link and share it with fellow artists</p>
              </div>

              <div className="text-center space-y-4">
                <div className="w-16 h-16 mx-auto rounded-full bg-primary/20 flex items-center justify-center">
                  <span className="text-2xl font-bold text-primary">2</span>
                </div>
                <h3 className="text-xl font-semibold text-foreground dark:text-white">Artist Signs Up</h3>
                <p className="text-muted-foreground">When they join and activate a paid plan, you both get rewarded</p>
              </div>

              <div className="text-center space-y-4">
                <div className="w-16 h-16 mx-auto rounded-full bg-primary/20 flex items-center justify-center">
                  <span className="text-2xl font-bold text-primary">3</span>
                </div>
                <h3 className="text-xl font-semibold text-foreground dark:text-white">Earn Credits</h3>
                <p className="text-muted-foreground">Apply your credits for 50% off your next monthly invoice</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

export default Affiliates
